<script setup lang="ts">
import { computed } from "vue";
import PaginationButton from "@/core/components/table/PaginationButton.vue";

const props = withDefaults(
  defineProps<{
    totalEntries: number;
    goToPage: (page: number, pageSize: number) => Promise<void>;
    pageSize?: number;
    currentPage: number;
  }>(),
  {
    pageSize: 10,
  },
);

const emit = defineEmits<{
  "update:currentPage": [page: number];
}>();

const totalNumberOfPages = computed(() =>
  Math.max(1, Math.ceil(props.totalEntries / props.pageSize)),
);

const firstRecord = computed(() =>
  props.totalEntries === 0 ? 0 : (props.currentPage - 1) * props.pageSize + 1,
);
const lastRecord = computed(() =>
  Math.min(props.currentPage * props.pageSize, props.totalEntries),
);

async function changePage(page: number) {
  await props.goToPage(page, props.pageSize);
  emit("update:currentPage", page);
}
</script>

<template>
  <div
    class="p-6 flex flex-col gap-3 sm:flex-row justify-between items-center bg-[#fafafa] rounded-lg"
  >
    <div class="text-[#666] text-sm">
      {{
        $t("common.table.pagination.showing", {
          start: firstRecord,
          end: lastRecord,
          total: totalEntries,
        })
      }}
    </div>

    <div class="flex items-center flex-wrap gap-2">
      <PaginationButton
        title="<<"
        :goToPage="() => changePage(1)"
        :isClickable="currentPage !== 1"
      />

      <PaginationButton
        title="<"
        :goToPage="() => changePage(currentPage - 1)"
        :isClickable="currentPage !== 1"
      />

      <button
        class="px-3 py-2 border rounded text-sm bg-[#28b473] text-white border-[#28b473]"
      >
        {{ currentPage }}
      </button>

      <PaginationButton
        title=">"
        :goToPage="() => changePage(currentPage + 1)"
        :isClickable="currentPage !== totalNumberOfPages"
      />

      <PaginationButton
        title=">>"
        :goToPage="() => changePage(totalNumberOfPages)"
        :isClickable="currentPage !== totalNumberOfPages"
      />
    </div>
  </div>
</template>
